import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Crown, ShieldCheck, Sparkles, Heart, CalendarCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import api from "../api";
import { MEMBERSHIP_BENEFITS, BRAND, STAGES } from "../mock";

const MONTHS = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
function formatSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return `${d.getDate()} de ${MONTHS[d.getMonth()]} de ${d.getFullYear()}`;
}

export default function Membresia() {
  const { isAuthenticated, isMember, user, cancelSubscription } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState("");

  const status = user?.membershipStatus;
  const since = formatSince(user?.membershipSince);

  const handleJoin = async () => {
    if (!isAuthenticated) {
      navigate("/registro");
      return;
    }
    if (!selectedStage) {
      toast({ title: "Elige tu espacio", description: "Selecciona embarazo, puerperio o menopausia para continuar." });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/create-checkout", {
        origin_url: window.location.origin,
        stage: selectedStage,
      });
      window.location.href = data.url;
    } catch (e) {
      setLoading(false);
      toast({ title: "Ups", description: e.response?.data?.detail || "No se pudo iniciar el pago." });
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    const res = await cancelSubscription();
    setCancelling(false);
    setConfirmOpen(false);
    if (!res.ok) {
      toast({ title: "Ups", description: res.error });
    }
  };

  // ---------- FAREWELL (membership canceled) ----------
  if (isAuthenticated && !isMember && status === "canceled") {
    return (
      <div className="animate-fade-in min-h-[70vh] flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-2xl bg-white rounded-[2.5rem] shadow-xl border border-amber/30 p-10 md:p-14 text-center">
          <div className="w-20 h-20 rounded-full bg-rose/20 flex items-center justify-center mx-auto">
            <Heart size={38} className="text-terracotta fill-terracotta/30" />
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-plum mt-7 leading-tight">
            Gracias por caminar con nosotras
          </h1>
          <p className="font-body text-lg text-ink/70 mt-6 leading-relaxed">
            {user?.name?.split(" ")[0] ? `${user.name.split(" ")[0]}, tu` : "Tu"} presencia ha sido un regalo para
            esta comunidad. Cada palabra que compartiste, cada silencio sostenido
            y cada abrazo virtual han hecho de este espacio un lugar más cálido.
          </p>
          <p className="font-body text-lg text-ink/70 mt-4 leading-relaxed">
            Tu suscripción ha sido cancelada. Te deseamos, de todo corazón, lo
            mejor en la nueva etapa que está por venir. Las puertas de esta casa
            quedan siempre abiertas para ti.
          </p>
          <blockquote className="mt-8 border-l-4 border-terracotta pl-5 text-left">
            <p className="font-serif italic text-2xl text-plum">
              “Ninguna mujer debería transitar sola sus etapas. Juntas nos
              sostenemos, nos escuchamos y nos transformamos.”
            </p>
          </blockquote>
          <div className="flex flex-wrap gap-4 justify-center mt-9">
            <Link to="/articulos" className="border border-plum/30 text-plum font-sans px-7 py-3 rounded-full hover:bg-plum/5 transition-colors">
              Seguir leyendo artículos
            </Link>
            <button onClick={handleJoin} disabled={loading} className="bg-plum text-cream font-sans px-7 py-3 rounded-full hover:bg-[#684d89] transition-colors disabled:opacity-60">
              {loading ? "Redirigiendo..." : "Volver a la comunidad"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- MEMBER VIEW (active) ----------
  if (isAuthenticated && isMember) {
    return (
      <div className="animate-fade-in">
        <section className="bg-gradient-to-b from-amber/30 to-cream">
          <div className="max-w-3xl mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
            <span className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-terracotta bg-terracotta/10 px-4 py-2 rounded-full">
              <Crown size={14} /> Miembro de la comunidad
            </span>
            <h1 className="font-serif text-5xl md:text-6xl text-plum mt-5">
              {user?.name?.split(" ")[0] ? `Hola, ${user.name.split(" ")[0]}` : "Hola"}
            </h1>
            <p className="font-body text-lg text-ink/65 mt-4">
              Gracias por formar parte de este espacio. Tu membresía está activa.
            </p>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-5 lg:px-8 py-12">
          <div className="bg-white rounded-[2rem] shadow-xl border border-amber/30 p-9">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="font-sans text-xs uppercase tracking-[0.15em] text-terracotta">Estado</p>
                <p className="font-serif text-3xl text-plum flex items-center gap-2">
                  Membresía activa
                </p>
              </div>
              <div className="text-right">
                <p className="font-sans text-xs uppercase tracking-[0.15em] text-terracotta">Cuota</p>
                <p className="font-serif text-3xl text-plum">{BRAND.membershipPrice}{BRAND.currency}<span className="font-body text-base text-ink/50">/mes</span></p>
              </div>
            </div>

            {since && (
              <p className="flex items-center gap-2 font-body text-ink/60 mt-5">
                <CalendarCheck size={17} className="text-plum" /> Miembro desde el {since}
              </p>
            )}
            {user?.stage && (
              <p className="flex items-center gap-2 font-body text-ink/70 mt-2">
                <Sparkles size={17} className="text-terracotta" /> Tu espacio:{" "}
                <span className="font-semibold text-plum capitalize">{user.stage}</span>
              </p>
            )}

            <div className="h-px bg-amber/40 my-7" />

            <p className="font-sans uppercase tracking-[0.15em] text-xs text-terracotta mb-3">Tienes acceso a</p>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {MEMBERSHIP_BENEFITS.map((b) => (
                <div key={b} className="flex items-start gap-2.5 font-body text-ink/75">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-plum/10 flex items-center justify-center shrink-0">
                    <Check size={12} className="text-plum" />
                  </span>
                  {b}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/cursos" className="bg-plum text-cream font-sans px-6 py-3 rounded-full hover:bg-[#684d89] transition-colors">
                Ir a los cursos
              </Link>
              <Link to="/sesiones" className="border border-plum/30 text-plum font-sans px-6 py-3 rounded-full hover:bg-plum/5 transition-colors">
                Ver sesiones
              </Link>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => setConfirmOpen(true)}
              className="font-body text-sm text-ink/45 hover:text-terracotta transition-colors underline underline-offset-4"
            >
              Cancelar mi suscripción
            </button>
          </div>
        </section>

        {confirmOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-5 bg-ink/60 backdrop-blur-sm animate-fade-in" onClick={() => setConfirmOpen(false)}>
            <div className="bg-cream rounded-[2rem] max-w-md w-full p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-serif text-3xl text-plum">¿Seguro que quieres cancelar?</h3>
              <p className="font-body text-ink/65 mt-3">
                Perderás el acceso a los cursos, los foros y las sesiones grupales.
                Podrás volver cuando quieras, siempre serás bienvenida.
              </p>
              <div className="flex gap-3 mt-7">
                <button
                  onClick={() => setConfirmOpen(false)}
                  className="flex-1 border border-plum/30 text-plum font-sans py-3 rounded-full hover:bg-plum/5 transition-colors"
                >
                  Mantener membresía
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="flex-1 bg-terracotta text-cream font-sans py-3 rounded-full hover:bg-[#b5624d] transition-colors disabled:opacity-60"
                >
                  {cancelling ? "Cancelando..." : "Sí, cancelar"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- SALES VIEW (not member) ----------
  return (
    <div className="animate-fade-in">
      <section className="bg-gradient-to-b from-amber/30 to-cream">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
          <span className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-terracotta bg-terracotta/10 px-4 py-2 rounded-full">
            <Sparkles size={14} /> Comunidad <span className="font-serif italic normal-case text-xl leading-none ml-1.5">matrum</span>
          </span>
          <h1 className="font-serif text-5xl md:text-6xl text-plum mt-5">
            Únete a la comunidad
          </h1>
          <p className="font-body text-lg text-ink/65 mt-4 max-w-2xl mx-auto">
            Un espacio seguro para acompañarte en tu embarazo, puerperio o
            menopausia, con contenido exclusivo y en compañía de otras mujeres.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-14">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-serif text-4xl text-plum">Todo lo que incluye</h2>
            <ul className="mt-7 space-y-4">
              {MEMBERSHIP_BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3 font-body text-ink/75 text-lg">
                  <span className="mt-1 w-6 h-6 rounded-full bg-plum/10 flex items-center justify-center shrink-0">
                    <Check size={14} className="text-plum" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="relative rounded-[2rem] bg-plum text-cream p-9 shadow-2xl">
            <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-terracotta/30 blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.18em] text-amber">
                <Crown size={15} /> Membresía completa
              </span>
              <div className="flex items-end gap-2 mt-5">
                <span className="font-serif text-6xl">{BRAND.membershipPrice}{BRAND.currency}</span>
                <span className="font-body text-cream/70 mb-2">/ mes</span>
              </div>
              <p className="font-body text-cream/70 mt-2">Sin permanencia · Cancela cuando quieras</p>

              <div className="h-px bg-cream/20 my-7" />

              <p className="font-sans text-xs uppercase tracking-[0.15em] text-amber mb-3">
                Elige tu espacio
              </p>
              <div className="grid grid-cols-3 gap-2 mb-6">
                {STAGES.map((s) => {
                  const colorMap = {
                    embarazo: "bg-rose text-plum",
                    puerperio: "bg-terracotta text-cream",
                    menopausia: "bg-plum text-cream border border-cream/40",
                  };
                  const isSel = selectedStage === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSelectedStage(s.id)}
                      className={`font-sans text-sm py-2.5 rounded-xl transition-all duration-200 ${colorMap[s.id]} ${
                        isSel
                          ? "ring-2 ring-cream ring-offset-2 ring-offset-plum font-semibold scale-105 shadow-lg"
                          : "opacity-70 hover:opacity-100"
                      }`}
                    >
                      {s.title}
                    </button>
                  );
                })}
              </div>
              <p className="font-body text-xs text-cream/60 -mt-3 mb-5">
                Accederás a los cursos, la comunidad y las sesiones de tu etapa.
              </p>

              <button
                onClick={handleJoin}
                disabled={loading}
                className="w-full bg-amber text-plum font-sans font-semibold py-4 rounded-full hover:bg-[#e0b88a] transition-colors text-lg disabled:opacity-60"
              >
                {loading ? "Redirigiendo..." : isAuthenticated ? "Continuar al pago" : "Crear cuenta y unirme"}
              </button>

              <p className="flex items-center justify-center gap-2 font-body text-xs text-cream/60 mt-5">
                <ShieldCheck size={15} className="text-amber" /> Pago seguro con Stripe
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
